import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import commonActions from '../../../Actions/commonActions';
import priceSimulationActions from '../../../Actions/priceSimulationActions';
import { priceSimulationActionTypes } from '../../../ActionTypes/priceSimulationActionTypes';
import CardJS from '../../../Components/Card/Card';
import { Button } from '../../../Components/Fundamentals/Button';
import EmptyState from '../../../Components/Fundamentals/EmptyState/EmptyState';
import SpinnerJS from '../../../Components/Fundamentals/Spinner/Spinner';
import AnalysisTabContent from '../../../Components/PriceSimulation/AnalysisTabContent';
import '../../../Components/PriceSimulation/PriceSimulation.css';
import PriceSimulationWorksheet from '../../../Components/PriceSimulation/PriceSimulationWorksheet';
import PricingFilterBar from '../../../Components/PriceSimulation/PricingFilterBar';
import PricingSimulationHeader from '../../../Components/PriceSimulation/PricingSimulationHeader';
import { findPackagesMatchingFilters } from '../../../Components/PriceSimulation/packageMatching';
import { extractDraftModifications, toAdjustmentKeys } from '../../../Components/PriceSimulation/proposalPayloadExtractor';
import type { PackageProposalPayload } from '../../../Components/PriceSimulation/proposalPayloadExtractor';
import { calculateRoundProgress } from '../../../Components/PriceSimulation/roundProgress';
import { calculateWorksheet } from '../../../Components/PriceSimulation/worksheetCalculations';
import { computeSkuFilterSelection } from '../../../Components/PriceSimulation/skuInstances';
import SendPriceForApprovalModal from '../../../Components/PriceSimulation/SendPriceForApprovalModal';
import SimulationTabContent from '../../../Components/PriceSimulation/SimulationTabContent';
import TabsJS from '../../../Components/Tabs/Tabs';
import Modals from '../../../Constants/modals';
import usePackageContexts from '../../../Hooks/Price-simulation/usePackageContexts';
import usePriceSimulationFilters from '../../../Hooks/Price-simulation/usePriceSimulationFilters';
import useWorksheetAdjustments from '../../../Hooks/Price-simulation/useWorksheetAdjustments';
import useModal from '../../../Hooks/useModal';
import { useAppDispatch } from '../../../Store/types';
import type { FilterOption, RootState } from '../../../Components/PriceSimulation/PriceSimulation.types';
import type { WorksheetFilters } from '../../../Components/PriceSimulation/PriceSimulationWorksheet.types';

interface TabItem {
    id: string;
    label: string | null;
}

type CardChild = ReactElement | false | null | undefined;
const Card = CardJS as unknown as ComponentType<{ children: ReactNode }> & { Content: ComponentType<{ isLoading?: boolean; children: CardChild | CardChild[] }> };
const Tabs = TabsJS as unknown as ComponentType<{ tabs: TabItem[]; activeTab: TabItem; setActiveTab: (tab: TabItem) => void }>;
const Spinner = SpinnerJS as unknown as ComponentType<{ size?: string; position?: string }>;

const PriceSimulationDetailContainer = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { openModal, isModalOpen } = useModal();

    const packages = useSelector((state: RootState) => state.PriceSimulationReducer.packages);
    const packageDetailsMap = useSelector((state: RootState) => state.PriceSimulationReducer.packageDetailsMap);
    const packageMetadataMap = useSelector((state: RootState) => state.PriceSimulationReducer.packageMetadataMap);
    const isLoading = useSelector((state: RootState) => state.CommonReducer.dataLoading.packages);

    const filters = usePriceSimulationFilters();
    const { selectedPackages, selectedPackageIds, worksheetPackages, displayPackages } = filters;
    const {
        isLoadingPackages,
        unloadedPackageNames,
        failedUnselectedCount,
        allPackagesLoaded,
        retry: retryLoadingPackages,
    } = usePackageContexts(filters.packageOptions, selectedPackages);
    const adjustmentState = useWorksheetAdjustments(worksheetPackages);
    const { adjustments, resetAdjustments, discardAdjustments } = adjustmentState;

    const [activeTab, setActiveTab] = useState<TabItem>(() => ({ id: 'simulation', label: t('priceSimulation.simulation') }));
    const [packagesVisible, setPackagesVisible] = useState(true);
    const [expandedPackages, setExpandedPackages] = useState<Record<string, boolean>>({});
    const [expandedTiers, setExpandedTiers] = useState<Record<string, boolean>>({});
    const [isSaving, setIsSaving] = useState(false);

    const isOutdatedVersion = selectedPackages.some((pkg) => pkg.id in packageMetadataMap && packageMetadataMap[pkg.id].isOutdatedVersion);

    const tabs: TabItem[] = [
        { id: 'simulation', label: t('priceSimulation.simulation') },
        { id: 'analysis', label: t('priceSimulation.analysis') },
    ];

    useEffect(() => {
        if (!packages || packages.length === 0) {
            void dispatch(priceSimulationActions.getPackages());
        }
    }, [packages, dispatch]);

    useEffect(() => {
        setExpandedPackages((prev) => ({ ...Object.fromEntries(displayPackages.map((pkg) => [pkg.id, true])), ...prev }));
        setExpandedTiers((prev) => ({ ...Object.fromEntries(displayPackages.flatMap((pkg) => pkg.tiers.map((tier) => [tier.id, false]))), ...prev }));
    }, [displayPackages]);

    const { setSelectedPackages, setSelectedTiers, setSelectedProducts } = filters;
    const handleFilterBySku = useCallback(
        (sku: string) => {
            const selection = computeSkuFilterSelection(sku, filters.skuInstancesMap[sku] ?? [], selectedPackages);
            setSelectedPackages(selection.packages);
            setSelectedTiers(selection.tiers);
            setSelectedProducts(selection.products);
        },
        [filters.skuInstancesMap, selectedPackages, setSelectedPackages, setSelectedTiers, setSelectedProducts]
    );

    const {
        packageOptions,
        allPackageWorksheets,
        modificationStatuses,
        selectedPlans,
        selectedProductTypes,
        selectedProducts,
        onlyPlansWithVolume,
        setModificationStatuses,
        setSelectedPlans,
        setSelectedProductTypes,
    } = filters;
    const withPackageAutoSelect =
        (key: 'modificationStatuses' | 'selectedPlans' | 'selectedProductTypes' | 'selectedProducts', setValue: (options: FilterOption[]) => void) => (options: FilterOption[]) => {
            setValue(options);
            const isAllStatuses = key === 'modificationStatuses' && options.some((option) => option.id === 'All');
            if (!allPackagesLoaded || selectedPackages.length > 0 || options.length === 0 || isAllStatuses) return;

            const worksheetFilters: WorksheetFilters = { modificationStatuses, selectedPlans, selectedProductTypes, selectedProducts, onlyPlansWithVolume, [key]: options };
            const matching = findPackagesMatchingFilters(allPackageWorksheets, packageOptions, adjustments, worksheetFilters);
            if (matching.length === 0) {
                dispatch(commonActions.showWarning(t('priceSimulation.noPackagesMatchFilter')));
                return;
            }
            setSelectedPackages(matching);
        };

    const roundProgress = useMemo(() => (allPackagesLoaded ? calculateRoundProgress(packageDetailsMap, adjustments) : null), [allPackagesLoaded, packageDetailsMap, adjustments]);

    const calculations = useMemo(
        () => calculateWorksheet(worksheetPackages, adjustments, { modificationStatuses, selectedPlans, selectedProductTypes, selectedProducts, onlyPlansWithVolume }),
        [worksheetPackages, adjustments, modificationStatuses, selectedPlans, selectedProductTypes, selectedProducts, onlyPlansWithVolume]
    );

    const noPackagesSelected = (
        <EmptyState
            icon="calculator"
            title={t('priceSimulation.selectPackageToBegin')}
            description={t('priceSimulation.selectPackagePromptSubtitle') ?? undefined}
            className="simulation-empty-state bg-default border rounded-8 mt-24"
        />
    );

    const noPlansMatch = (
        <div className="mt-24">
            <EmptyState icon="filter" title={t('priceSimulation.noPlansMatch')} description={t('priceSimulation.noPlansMatchDescription') ?? undefined} />
        </div>
    );

    const noPlansMatchFilters = selectedPackages.length > unloadedPackageNames.length && calculations.packages.length === 0;

    const isWaitingForPackages = isLoadingPackages && worksheetPackages.length === 0;

    const packagesSpinner = isLoadingPackages && (
        <div className={isWaitingForPackages ? 'p-24' : 'p-16'}>
            <Spinner />
        </div>
    );

    const unloadedPackagesWarning = unloadedPackageNames.length > 0 && (
        <div className="alert alert-warning mt-16" role="alert">
            <p>{t('priceSimulation.packagesNotLoaded', { packages: unloadedPackageNames.join(', ') })}</p>
            <Button onClick={retryLoadingPackages}>{t('priceSimulation.retry')}</Button>
        </div>
    );

    const handleSaveDraft = useCallback(async () => {
        const payloads = extractDraftModifications(packageDetailsMap, selectedPackageIds, adjustments);

        if (payloads.length === 0) {
            dispatch(commonActions.showWarning(t('priceSimulation.noChangesToSave')));
            return;
        }

        setIsSaving(true);
        try {
            await dispatch(priceSimulationActions.saveDraftProposals(payloads));
            resetAdjustments();
            dispatch({ type: priceSimulationActionTypes.INVALIDATE_PACKAGE_CONTEXTS, packageIds: selectedPackageIds });
            retryLoadingPackages();
            dispatch(commonActions.showSuccess(t('priceSimulation.draftSaved')));
        } catch {
            dispatch(commonActions.showError(t('priceSimulation.draftSaveFailed')));
        } finally {
            setIsSaving(false);
        }
    }, [selectedPackageIds, packageDetailsMap, adjustments, resetAdjustments, retryLoadingPackages, dispatch, t]);

    const handleApprovalSubmitted = useCallback(
        (submitted: PackageProposalPayload[]) => {
            discardAdjustments(toAdjustmentKeys(submitted));
        },
        [discardAdjustments]
    );

    return (
        <div className="price-simulation-detail">
            <PricingSimulationHeader
                onSaveDraft={() => void handleSaveDraft()}
                isSaving={isSaving}
                onSendForApproval={() => openModal(Modals.SendPriceForApprovalModal)}
                roundProgress={roundProgress}
            />

            {isOutdatedVersion && (
                <div className="alert alert-warning alert-sm" role="alert">
                    <p>{t('priceSimulation.outdatedVersionWarning')}</p>
                </div>
            )}

            <Card>
                <Card.Content isLoading={isLoading}>
                    <PricingFilterBar
                        productFilters={filters.selectedProducts}
                        onProductFiltersChange={withPackageAutoSelect('selectedProducts', setSelectedProducts)}
                        selectedPackages={selectedPackages}
                        onPackagesChange={filters.setSelectedPackages}
                        selectedProductTypes={filters.selectedProductTypes}
                        onProductTypesChange={withPackageAutoSelect('selectedProductTypes', setSelectedProductTypes)}
                        selectedTiers={filters.selectedTiers}
                        onTiersChange={filters.setSelectedTiers}
                        selectedPlans={filters.selectedPlans}
                        onPlansChange={withPackageAutoSelect('selectedPlans', setSelectedPlans)}
                        modificationStatuses={filters.modificationStatuses}
                        onModificationStatusesChange={withPackageAutoSelect('modificationStatuses', setModificationStatuses)}
                        onlyPlansWithVolume={filters.onlyPlansWithVolume}
                        onOnlyPlansWithVolumeChange={filters.setOnlyPlansWithVolume}
                        productOptions={filters.productOptions}
                        packageOptions={filters.packageOptions}
                        productTypeOptions={filters.productTypeOptions}
                        tierOptions={filters.tierOptions}
                        planOptions={filters.planOptions}
                        modificationStatusOptions={filters.modificationStatusOptions}
                        allPackagesLoaded={allPackagesLoaded}
                    />
                </Card.Content>
            </Card>

            {failedUnselectedCount > 0 && (
                <div className="alert alert-warning alert-sm mt-16" role="alert">
                    <p>{t('priceSimulation.unselectedPackagesNotLoaded', { count: failedUnselectedCount })}</p>
                    <Button onClick={retryLoadingPackages}>{t('priceSimulation.retry')}</Button>
                </div>
            )}

            <div className="mt-24">
                <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />

                {activeTab.id === 'simulation' &&
                    (selectedPackages.length === 0 ? (
                        noPackagesSelected
                    ) : (
                        <>
                            {packagesSpinner}
                            {/* Hidden rather than unmounted while packages reload (e.g. after submit), so the worksheet keeps its collapsed sections */}
                            <div className={isWaitingForPackages ? 'd-none' : undefined}>
                                <SimulationTabContent
                                    packages={displayPackages}
                                    expandedPackages={expandedPackages}
                                    expandedTiers={expandedTiers}
                                    onTogglePackage={(packageId: string) => setExpandedPackages((prev) => ({ ...prev, [packageId]: !prev[packageId] }))}
                                    onToggleTier={(tierId: string) => setExpandedTiers((prev) => ({ ...prev, [tierId]: !prev[tierId] }))}
                                    isVisible={packagesVisible}
                                    onToggleVisibility={() => setPackagesVisible((prev) => !prev)}
                                    selectedPeriod={filters.billingCycle}
                                    onPeriodChange={filters.setBillingCycle}
                                />

                                {unloadedPackagesWarning}

                                {noPlansMatchFilters && noPlansMatch}

                                {worksheetPackages.length > 0 && (
                                    <div className={noPlansMatchFilters ? 'd-none' : undefined}>
                                        <PriceSimulationWorksheet
                                            calculations={calculations}
                                            adjustmentState={adjustmentState}
                                            skuInstancesMap={filters.skuInstancesMap}
                                            onFilterBySku={handleFilterBySku}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    ))}

                {activeTab.id === 'analysis' &&
                    (selectedPackages.length === 0 ? (
                        noPackagesSelected
                    ) : (
                        <>
                            {packagesSpinner}
                            {!isWaitingForPackages && (
                                <>
                                    {unloadedPackagesWarning}
                                    {noPlansMatchFilters && noPlansMatch}
                                    {calculations.packages.length > 0 && <AnalysisTabContent calculations={calculations} />}
                                </>
                            )}
                        </>
                    ))}
            </div>

            {Boolean(isModalOpen(Modals.SendPriceForApprovalModal)) && (
                <SendPriceForApprovalModal adjustments={adjustments} selectedPackageIds={selectedPackageIds} onSubmitted={handleApprovalSubmitted} />
            )}
        </div>
    );
};

export default PriceSimulationDetailContainer;
